import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Box, HStack, Button, Flex } from "@chakra-ui/react";
import CDS from "design-system";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { EmailSummary } from "features/Resources/Email/models";
import { useEmailListQuery } from "features/Resources/Email/hooks";
import EmailMessageStatusIcon from "features/Resources/Shared/Components/EmailMessageStatusIcon";
import { basicShortDate } from "helpers/time";
import { Text12 } from "components/Text";
import MessageEmailThreeDotMenu from "features/Resources/Shared/Components/Button/MessageEmailThreeDotMenu";
import IssueTableIcon from "components/Icon/IssueTableIcon";
import { DisplayRecipients } from "../DisplayRecipients";
import NewHireJourneyTag from "features/NewHireJourney/Components/NewHireJourneyTag";

const columnHelper = createColumnHelper<EmailSummary>();

interface EmailsTableProps {
  queryString?: string;
  onExpand: (item: EmailSummary) => void;
}

export default function EmailsTable({ queryString = "", onExpand }: EmailsTableProps) {
  const { data, isLoading } = useEmailListQuery(queryString);

  const columns: ColumnDef<EmailSummary, any>[] = [
    columnHelper.accessor((row) => row, {
      id: "issues",
      header: "",
      cell: ({ getValue }) => {
        const email = getValue();
        const showIssues = email.status !== "delivered" && email.status !== "skipped" && email.status !== "removed";
        return <Flex align="center">{showIssues ? <IssueTableIcon issueIds={email.issueIds} /> : <Box />}</Flex>;
      },
      meta: {
        sx: {
          paddingRight: 0,
          width: "52px",
          maxWidth: "52px",
          minWidth: "52px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row, {
      id: "sendDate",
      header: "Scheduled",
      cell: ({ getValue }) => {
        const email = getValue();
        const timestamp = email.deliveredAt ?? email.deliverAt ?? email.attemptedAt;

        return (
          <HStack spacing={2} alignItems="center" justify="flex-start">
            <EmailMessageStatusIcon status={email.status} />
            <Text12>{timestamp ? basicShortDate(timestamp, email.timezone) : "—"}</Text12>
          </HStack>
        );
      },
      meta: {
        sx: {
          paddingLeft: 0,
          paddingRight: 0,
          width: "96px",
          maxWidth: "96px",
          minWidth: "96px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row, {
      header: "Name",
      minSize: 100,
      maxSize: 400,
      cell: ({ getValue }) => (
        <Button variant="unstyled" width="fit-content" onClick={() => onExpand(() => getValue())}>
          <Text12
            wordBreak="break-word"
            textDecorationLine={getValue()?.status === "skipped" ? "line-through" : "none"}
            fontWeight="medium"
            noOfLines={2}
          >
            {getValue()?.name}
          </Text12>
        </Button>
      ),
      meta: {
        sx: {
          paddingLeft: 0,
        },
        headerHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "recipient",
      header: "To",
      cell: ({ getValue }) => {
        const email = getValue();
        return <DisplayRecipients email={email} />;
      },
      meta: {
        sx: {
          paddingLeft: 0,
          width: "160px",
          maxWidth: "160px",
          minWidth: "160px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "journey",
      header: "New Hire",
      cell: ({ getValue }) => {
        const email = getValue();
        if (!email.newHireJourneyId) return null;
        return <NewHireJourneyTag journeyId={email.newHireJourneyId} size="md" clickable />;
      },
      meta: {
        sx: {
          width: "160px",
          maxWidth: "160px",
          minWidth: "160px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    // Spacer column to push actions to the right
    columnHelper.accessor((row) => row, {
      id: "spacer",
      header: "",
      cell: () => null,
      minSize: 0,
      meta: {
        sx: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "statusActions",
      header: "",
      cell: ({ getValue }) => {
        const email = getValue();
        return (
          <HStack gap="1">
            <MessageEmailThreeDotMenu prefixId={email.prefixId} />
          </HStack>
        );
      },
      meta: {
        sx: {
          width: "99px",
          maxWidth: "99px",
          minWidth: "99px",
        },
        headerHStackProps: { justifyContent: "flex-end" },
        cellHStackProps: { justifyContent: "flex-end" },
      },
      enableSorting: false,
    }),
  ];

  return (
    <ControlledDataTable<EmailSummary>
      data={data || []}
      isLoading={isLoading}
      columns={columns}
      emptyStateComponent={
        <Box textAlign="center" p={4}>
          <CDS.Text16 color="chakra-subtle-text">
            No emails found
          </CDS.Text16>
        </Box>
      }
      sx={{ backgroundColor: "transparent" }}
    />
  );
}
